import { useState, useRef } from "react";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export const ImageUpload = ({ path, onChange, testId = "image-upload" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { t } = useI18n();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.path);
    } catch (e) {
      toast.error(t("error_upload"));
    } finally {
      setUploading(false);
    }
  };

  if (path) {
    return (
      <div className="relative group rounded-md overflow-hidden border border-border">
        <img src={fileUrl(path)} alt="upload" className="w-full h-64 object-cover" />
        <button
          type="button"
          data-testid={`${testId}-remove`}
          onClick={() => onChange(null)}
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
      }}
      className="cursor-pointer border-2 border-dashed border-border rounded-md p-8 flex flex-col items-center justify-center gap-3 hover:border-foreground/30 hover:bg-secondary/50 transition-colors"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {uploading ? (
        <>
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t("uploading")}</span>
        </>
      ) : (
        <>
          <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-muted-foreground" strokeWidth={1.8} />
          </div>
          <div className="text-center">
            <div className="text-sm font-medium">Click or drag an image</div>
            <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP · max 10MB</div>
          </div>
        </>
      )}
    </div>
  );
};
